import React from 'react';
import { Box, Container, Typography, Divider, Link } from '@mui/material';
import AUHeader from '../components/AUHeader';

const DARK = '#0F1117';
const GRAY = '#64748B';
const BORDER = '#E2E8F0';
const BLUE = '#1A7FFF';

// Última revisión del contenido. Se muestra al pie para que se note si quedó viejo.
const ULTIMA_ACTUALIZACION = '25 de septiembre de 2026';
const EMAIL_CONTACTO = 'au.coachingservices@gmail.com';

const Seccion = ({ titulo, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, color: DARK, mb: 1.5, fontSize: { xs: '1.05rem', md: '1.15rem' } }}
    >
      {titulo}
    </Typography>
    <Box sx={{ color: GRAY, fontSize: '0.95rem', lineHeight: 1.75 }}>{children}</Box>
  </Box>
);

const P = ({ children }) => (
  <Typography component="p" sx={{ mb: 1.5, color: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}>
    {children}
  </Typography>
);

const Lista = ({ items }) => (
  <Box component="ul" sx={{ pl: 2.5, mb: 1.5, '& li': { mb: 0.75 } }}>
    {items.map((t, i) => (
      <Typography component="li" key={i} sx={{ color: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}>
        {t}
      </Typography>
    ))}
  </Box>
);

const PrivacyPage = () => (
  <Box sx={{ bgcolor: '#FFFFFF', minHeight: '100vh' }}>
    <AUHeader />

    <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
      <Typography
        variant="h4"
        sx={{ fontWeight: 800, color: DARK, mb: 1, fontSize: { xs: '1.75rem', md: '2.25rem' } }}
      >
        Política de privacidad
      </Typography>
      <Typography sx={{ color: GRAY, mb: 4, fontSize: '0.95rem' }}>
        Cómo AU Coaching Services trata tus datos personales.
      </Typography>

      <Divider sx={{ mb: 4, borderColor: BORDER }} />

      <Seccion titulo="Quién trata tus datos">
        <P>
          AU Coaching Services, servicio de coaching online de pérdida de grasa y ganancia de músculo
          dirigido por Agustín Urzúa, con operación en Chile. Para cualquier consulta sobre tus datos
          puedes escribir a{' '}
          <Link href={`mailto:${EMAIL_CONTACTO}`} sx={{ color: BLUE, fontWeight: 600 }}>
            {EMAIL_CONTACTO}
          </Link>
          .
        </P>
      </Seccion>

      <Seccion titulo="Qué datos recolectamos">
        <P>Solo los que tú nos entregas y los que necesitamos para prestarte el servicio:</P>
        <Lista
          items={[
            'Datos de contacto: nombre, correo electrónico, número de WhatsApp y usuario de Instagram.',
            'Respuestas del formulario de postulación: tu experiencia entrenando, tu situación actual, tu urgencia por empezar y qué te está frenando.',
            'Si llegas a ser cliente: datos de tu seguimiento, como peso, medidas, entrenamientos, alimentación, descanso y las fotos de progreso que decidas enviar.',
            'Los mensajes que nos envías por Instagram, si nos escribes por ahí.',
            'Datos técnicos de navegación: páginas visitadas, origen de la visita y un identificador anónimo del navegador.',
          ]}
        />
      </Seccion>

      <Seccion titulo="Para qué los usamos">
        <Lista
          items={[
            'Contactarte para coordinar tu reunión inicial y darle seguimiento a tu postulación, por WhatsApp, correo electrónico e Instagram.',
            'Evaluar si el servicio calza con tu caso.',
            'Prestarte el servicio de coaching si llegas a ser cliente.',
            'Enviarte contenido y novedades por correo, de lo que puedes darte de baja en cualquier momento.',
            'Medir cómo funciona nuestra publicidad y nuestro sitio, de forma agregada.',
          ]}
        />
        <P>
          No vendemos tus datos, no los cedemos a terceros con fines comerciales y no los usamos para
          fines distintos de los descritos acá.
        </P>
      </Seccion>

      <Seccion titulo="Con quién los compartimos">
        <P>
          Solo con los proveedores que hacen funcionar el servicio, y únicamente con lo que cada uno
          necesita:
        </P>
        <Lista
          items={[
            'Google (Sheets y Calendar): almacenamiento de tus datos de postulación y agendamiento de tu reunión.',
            'Meta (WhatsApp Business): envío de los mensajes de coordinación de tu reunión.',
            'Meta (Instagram): lectura de los mensajes directos que nos envías, para responderte y darle seguimiento a la conversación.',
            'Anthropic (Claude): herramienta de inteligencia artificial que usamos para ordenar y resumir tus mensajes y tu postulación antes de tu reunión.',
            'Resend: envío de correos.',
            'Flow: procesamiento de pagos, si contratas el servicio. No almacenamos datos de tu tarjeta.',
            'Vercel y Render: alojamiento del sitio y del servidor.',
          ]}
        />
      </Seccion>

      <Seccion titulo="Consentimiento para contactarte">
        <P>
          Al enviar el formulario de postulación aceptas que te contactemos por WhatsApp y correo
          electrónico para coordinar tu reunión inicial y darle seguimiento a tu postulación. Puedes
          pedirnos que dejemos de contactarte en cualquier momento, respondiendo el mismo WhatsApp,
          usando el enlace de baja de los correos, o escribiendo a la dirección de contacto.
        </P>
      </Seccion>

      <Seccion titulo="Cuánto tiempo los guardamos">
        <P>
          Mientras exista una relación contigo o interés de tu parte en el servicio, y después el
          tiempo que la ley nos obligue a conservarlos. Si nos pides que los eliminemos, lo hacemos
          salvo aquello que estemos obligados a mantener.
        </P>
      </Seccion>

      <Seccion titulo="Tus derechos">
        <P>
          De acuerdo a la Ley 19.628 sobre protección de la vida privada, puedes pedirnos en cualquier
          momento:
        </P>
        <Lista
          items={[
            'Acceder a los datos que tenemos sobre ti.',
            'Corregirlos si están equivocados o incompletos.',
            'Eliminarlos.',
            'Oponerte a que los usemos para contactarte.',
          ]}
        />
        <P>
          Escríbenos a{' '}
          <Link href={`mailto:${EMAIL_CONTACTO}`} sx={{ color: BLUE, fontWeight: 600 }}>
            {EMAIL_CONTACTO}
          </Link>{' '}
          y respondemos dentro de los plazos que exige la ley.
        </P>
      </Seccion>

      <Seccion titulo="Seguridad">
        <P>
          Tus datos se guardan en servicios con acceso restringido y protegido por credenciales. El
          acceso está limitado a Agustín Urzúa y al coach que lleva tu proceso.
        </P>
      </Seccion>

      <Seccion titulo="Cambios a esta política">
        <P>
          Si cambiamos cómo tratamos tus datos, actualizamos esta página y su fecha de última
          modificación.
        </P>
      </Seccion>

      <Divider sx={{ mt: 5, mb: 2, borderColor: BORDER }} />
      <Typography sx={{ color: GRAY, fontSize: '0.85rem' }}>
        Última actualización: {ULTIMA_ACTUALIZACION}
      </Typography>
    </Container>
  </Box>
);

export default PrivacyPage;
